import { useCallback } from 'react';

import { createHook, createStore } from 'react-sweet-state';

import { type DocNode } from '@atlaskit/adf-schema';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	encodeAdf,
	encodeQuotedSkillTagsToAdf,
	encodePromptToAdf,
	encodeSkillPromptToAdf,
	PROMPT_PLACEHOLDER_CHAR,
} from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
type SkillMetadataMap = Record<
	string,
	{ name: string; description: string; [key: string]: unknown }
>;

export type PlaceholderType = 'person' | 'link' | 'skill' | 'generic';

type PlaceholderData = {
	prompt: string;
	adfDocument?: DocNode;
	type: PlaceholderType;
	selectionStartIndex: number;
	selectionEndIndex: number;
	// Places placeholder type at the cursor position.
	// But should only be used as a last resort, if the placeholder is not inserted at the cursor position by default.
	insertAtCursor?: boolean;
};

type State = {
	placeholder: PlaceholderData | null;
};

const state = createStore({
	initialState: {
		placeholder: null,
	} as State,
	actions: {
		setPlaceholder:
			(placeholder: State['placeholder']) =>
			({ setState }) => {
				setState({ placeholder });
			},
	},
});

// exported for testing
export const usePlaceholderStore = createHook(state);

const getPlaceholderType = (placeholderLabel: string): PlaceholderType => {
	switch (placeholderLabel) {
		case 'person':
			return 'person';
		case 'skill':
			return 'skill';
		default:
			return 'link';
	}
};

const isPromptWithPlaceholder = (prompt: string): boolean => {
	return prompt.includes(PROMPT_PLACEHOLDER_CHAR);
};

export const useEditorPlaceholder = () => {
	const [{ placeholder }, { setPlaceholder }] = usePlaceholderStore();

	const insertPlaceholder = useCallback(
		(prompt: string, overrideType?: PlaceholderType, skillMetadata?: SkillMetadataMap): void => {
			if (overrideType === 'skill' && skillMetadata && !fg('rovo_chat_encode_adf_with_skills')) {
				const { adf, promptLength, placeholderRange } = encodeQuotedSkillTagsToAdf(
					prompt,
					skillMetadata,
				);

				setPlaceholder({
					prompt: placeholderRange
						? prompt.replace(new RegExp(PROMPT_PLACEHOLDER_CHAR, 'g'), '')
						: prompt,
					adfDocument: adf,
					type: placeholderRange ? getPlaceholderType(placeholderRange.label) : 'generic',
					selectionStartIndex: placeholderRange?.start ?? promptLength,
					selectionEndIndex: placeholderRange?.end ?? promptLength,
				});
				return;
			}

			if (overrideType === 'skill') {
				const { adf, promptLength } = encodeSkillPromptToAdf(prompt);
				setPlaceholder({
					prompt,
					adfDocument: adf,
					type: 'generic',
					selectionStartIndex: promptLength,
					selectionEndIndex: promptLength,
				});
				return;
			}

			if (fg('rovo_chat_encode_adf_with_skills')) {
				const { adf, promptLength, placeholderRange } = encodePromptToAdf(prompt, skillMetadata);

				const type: PlaceholderType = placeholderRange
					? overrideType ?? getPlaceholderType(placeholderRange.label)
					: 'generic';

				setPlaceholder({
					prompt,
					adfDocument: adf,
					type,
					selectionStartIndex: placeholderRange?.start ?? promptLength + 1,
					selectionEndIndex: placeholderRange?.end ?? promptLength + 1,
				});
			} else {
				const startIndexOfPlaceholder = prompt.indexOf(PROMPT_PLACEHOLDER_CHAR);
				const endIndexOfPlaceholder = prompt.lastIndexOf(PROMPT_PLACEHOLDER_CHAR);
				if (startIndexOfPlaceholder !== -1 && endIndexOfPlaceholder !== -1) {
					const placeholderLabel = prompt.slice(startIndexOfPlaceholder + 1, endIndexOfPlaceholder);
					const placeholderType = overrideType ?? getPlaceholderType(placeholderLabel);
					const selectionStartIndex = startIndexOfPlaceholder + 1;
					const selectionEndIndex = endIndexOfPlaceholder;

					const promptWithoutBackticks = prompt.replace(
						new RegExp(PROMPT_PLACEHOLDER_CHAR, 'g'),
						'',
					);
					const adfDocument = encodeAdf(promptWithoutBackticks);

					setPlaceholder({
						prompt: promptWithoutBackticks,
						adfDocument,
						type: placeholderType,
						selectionStartIndex,
						selectionEndIndex,
					});
				} else {
					// A proper placeholder was not found
					setPlaceholder({
						prompt: prompt,
						type: 'generic',
						selectionStartIndex: prompt.length + 1,
						selectionEndIndex: prompt.length + 1,
					});
				}
			}
		},
		[setPlaceholder],
	);

	const insertInlineCard = useCallback(
		(url: string): void => {
			const adfDocument = {
				version: 1,
				type: 'doc',
				content: [
					{
						type: 'paragraph',
						content: [
							{ type: 'inlineCard', attrs: { url } },
							{ type: 'text', text: ' ' },
						],
					},
				],
			} as DocNode;

			setPlaceholder({
				prompt: url,
				adfDocument,
				type: 'generic',
				selectionStartIndex: 3,
				selectionEndIndex: 3,
			});
		},
		[setPlaceholder],
	);

	/**
	 * Inserts a placeholder with a pre-built ADF document.
	 * Places cursor at start of content (position 2) to avoid position calculation issues.
	 * Use this when you have content with inline cards or other complex ADF nodes.
	 */
	const insertPlaceholderWithAdf = useCallback(
		(prompt: string, adfDocument: DocNode): void => {
			// Position 2 is always valid: doc(0) > paragraph(1) > content start(2)
			setPlaceholder({
				prompt,
				adfDocument,
				type: 'generic',
				selectionStartIndex: 2,
				selectionEndIndex: 2,
			});
		},
		[setPlaceholder],
	);

	const clearPlaceholder = useCallback((): void => {
		setPlaceholder(null);
	}, [setPlaceholder]);

	return {
		insertPlaceholder,
		insertPlaceholderWithAdf,
		insertInlineCard,
		clearPlaceholder,
		placeholder,
		isPromptWithPlaceholder,
	};
};
